import React from 'react';
import Grid from '@material-ui/core/Grid';
import { useTranslation } from 'react-i18next';
import { Field, FieldArray, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { array, object, string } from 'yup';
import { makeStyles } from '@material-ui/core/styles';
import EditModeCard, { EditModeCardSection } from '../../EditModeCard';
import TextArea from '../../Fields/TextArea';
import { contactsIdentifiers, TP } from '../../../constants/consts';
import FormikTextField from '../../Fields/TextField';
import FormikPhoneField from '../../Fields/FormikPhoneField';
import CountryCityBlock from '../../CountryCityBlock';
import { getIdObjectOrNull, getOption, scrollToInvalidField } from '../../../utils/common';
import {
  BOX_OFFICE_CATEGORY,
  BOX_OFFICE_EMAIL,
  BOX_OFFICE_PHONE,
  separateContactsForUpdate,
  setContactForUpdateAction,
} from '../../../utils/editCommon';
import EditFormFooter from '../EditFormFooter';

const useStyles = makeStyles(theme => ({
  singleField: {
    maxWidth: 400,
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  phoneField: {
    display: 'inline-block',
    width: '100%',
    top: 2,
  },
}));

const contactFields = {
  address: {
    name: 'address',
    value: '',
    label: `${TP}.m_ADDRESS`,
    typeId: contactsIdentifiers.address,
  },
  email: {
    name: 'email',
    value: '',
    label: `${TP}.m_EMAIL`,
    typeId: contactsIdentifiers.email,
  },
  phones: {
    name: 'phone',
    value: [],
    label: `${TP}.m_REGISTRATION_PHONENUMBER`,
    typeId: contactsIdentifiers.phone,
  },
  boxOfficeEmail: {
    name: BOX_OFFICE_EMAIL,
    value: [],
    label: `${TP}.FN_BOX_OFFICE_EMAIL`,
    typeId: contactsIdentifiers.email,
  },
  boxOfficePhone: {
    name: BOX_OFFICE_PHONE,
    value: [],
    label: `${TP}.FN_BOX_OFFICE_PHONE_NUMBER`,
    typeId: contactsIdentifiers.phone,
  },
};

const {
  address: { name: addressFieldName },
  email: { name: emailFieldName },
  phones: { name: phoneFieldName },
  boxOfficeEmail: { name: officeEmailsFieldName },
  boxOfficePhone: { name: officePhonesFieldName },
} = contactFields;

export const BasicContactsInfo = ({
  countriesOptions,
  contacts,
  submitForm,
  locationInfo,
  updatedAt,
  contactsUpdating,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const ContactsFormRef = React.createRef();

  const getInitialFormValue = React.useCallback(() => {
    const contactValues = {
      [addressFieldName]: { value: contactFields.address.value, typeId: contactFields.address.typeId },
      [emailFieldName]: { value: contactFields.email.value, typeId: contactFields.email.typeId },
      [phoneFieldName]: contactFields.phones.value,
      [officeEmailsFieldName]: contactFields.boxOfficeEmail.value,
      [officePhonesFieldName]: contactFields.boxOfficePhone.value,
    };

    contacts.forEach(contact => {
      switch (contact?.contactType?.id) {
        case contactsIdentifiers.address:
          contactValues[addressFieldName] = contact;
          break;
        case contactsIdentifiers.email:
          if (contact.contact_category === BOX_OFFICE_CATEGORY) {
            contactValues[officeEmailsFieldName] = [...contactValues[officeEmailsFieldName], contact];
          } else {
            contactValues[emailFieldName] = contact;
          }
          break;
        case contactsIdentifiers.phone:
          if (contact.contact_category === BOX_OFFICE_CATEGORY) {
            contactValues[officePhonesFieldName] = [...contactValues[officePhonesFieldName], contact];
          } else {
            contactValues[phoneFieldName] = [...contactValues[phoneFieldName], contact];
          }
          break;
        default:
          break;
      }
    });

    return {
      [addressFieldName]: contactValues[addressFieldName],
      country: locationInfo?.country ? getOption(locationInfo?.country) : null,
      city: locationInfo?.city ? getOption(locationInfo?.city) : null,
      [emailFieldName]: contactValues[emailFieldName],
      [phoneFieldName]:
        // eslint-disable-next-line no-nested-ternary
        contactValues[phoneFieldName]?.length > 0
          ? [...contactValues[phoneFieldName]]
          : [{ value: '', typeId: contactFields.phones.typeId }],
      [officeEmailsFieldName]:
        contactValues[officeEmailsFieldName]?.length > 0
          ? contactValues[officeEmailsFieldName]
          : [{ value: '', typeId: contactFields.boxOfficeEmail.typeId }],
      [officePhonesFieldName]:
        contactValues[officePhonesFieldName]?.length > 0
          ? contactValues[officePhonesFieldName]
          : [{ value: '', typeId: contactFields.boxOfficePhone.typeId }],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);

  const submitContacts = () => {
    if (ContactsFormRef?.current?.dirty) {
      const formValues = ContactsFormRef?.current?.values || {};
      const { newContacts, updatedContacts, deletedContacts } = separateContactsForUpdate([
        setContactForUpdateAction(addressFieldName, formValues[addressFieldName]),
        setContactForUpdateAction(emailFieldName, formValues[emailFieldName]),
        ...formValues[phoneFieldName]?.map(ph => setContactForUpdateAction(phoneFieldName, ph)),
        ...formValues[officeEmailsFieldName]?.map(ph => setContactForUpdateAction(officeEmailsFieldName, ph)),
        ...formValues[officePhonesFieldName].map(ph => setContactForUpdateAction(officePhonesFieldName, ph)),
      ]);
      submitForm({
        newContacts,
        updatedContacts,
        deletedContacts,
        country: getIdObjectOrNull(formValues.country?.value),
        city: getIdObjectOrNull(formValues.city?.value),
      });
    } else {
      submitForm();
    }
  };

  const submitFunc = () => {
    // eslint-disable-next-line no-unused-expressions
    ContactsFormRef.current?.submitForm();
    scrollToInvalidField();
  };

  return (
    <>
      <EditModeCard>
        <Formik
          initialValues={getInitialFormValue()}
          enableReinitialize
          validationSchema={basicContactsSchema}
          innerRef={ContactsFormRef}
          onSubmit={submitContacts}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <EditModeCardSection first>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      name={`${addressFieldName}.value`}
                      component={TextArea}
                      label={t(contactFields.address.label)}
                    />
                  </Grid>
                  <Grid container spacing={3} item sm={12} xs={12}>
                    <CountryCityBlock
                      countriesOptions={countriesOptions}
                      formValues={values}
                      setFieldValue={setFieldValue}
                      isCreatable
                    />
                  </Grid>
                </Grid>
              </EditModeCardSection>
              <EditModeCardSection>
                <Field
                  name={`${emailFieldName}.value`}
                  component={FormikTextField}
                  label={t(contactFields.email.label)}
                  className={classes.singleField}
                  trim
                />
                <div>
                  <FieldArray name={phoneFieldName}>
                    {() =>
                      values[phoneFieldName]?.map((phone, index) => (
                        <Field
                          name={`${phoneFieldName}.${index}.value`}
                          label={t(contactFields.phones.label)}
                          component={FormikPhoneField}
                          className={cn(classes.singleField, classes.phoneField)}
                          key={`${phoneFieldName}.${index}.value`}
                        />
                      ))
                    }
                  </FieldArray>
                </div>
              </EditModeCardSection>
              <EditModeCardSection>
                <FieldArray name={officeEmailsFieldName}>
                  {() =>
                    values[officeEmailsFieldName]?.map((email, index) => (
                      <Field
                        name={`${officeEmailsFieldName}.${index}.value`}
                        label={t(contactFields.boxOfficeEmail.label)}
                        component={FormikTextField}
                        className={classes.singleField}
                        key={`${officeEmailsFieldName}.${index}.value`}
                      />
                    ))
                  }
                </FieldArray>
                <div>
                  <FieldArray name={officePhonesFieldName}>
                    {() =>
                      values[officePhonesFieldName]?.map((phone, index) => (
                        <Field
                          name={`${officePhonesFieldName}.${index}.value`}
                          label={t(contactFields.boxOfficePhone.label)}
                          component={FormikPhoneField}
                          className={cn(classes.singleField, classes.phoneField)}
                          key={`${officePhonesFieldName}.${index}.value`}
                          trim
                        />
                      ))
                    }
                  </FieldArray>
                </div>
              </EditModeCardSection>
            </Form>
          )}
        </Formik>
      </EditModeCard>
      <EditFormFooter submitFunc={submitFunc} lastUpdatedDate={updatedAt} isLoading={contactsUpdating} />
    </>
  );
};

BasicContactsInfo.propTypes = {
  countriesOptions: PropTypes.array.isRequired,
  contacts: PropTypes.array.isRequired,
};

export default BasicContactsInfo;

const basicContactsSchema = () =>
  object().shape({
    country: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [emailFieldName]: object().shape({
      value: string()
        .trim()
        .nullable()
        .email(`${TP}.m_MAILNONVALID`),
    }),
    [officeEmailsFieldName]: array().of(
      object().shape({
        value: string()
          .trim()
          .nullable()
          .email(`${TP}.m_MAILNONVALID`),
      }),
    ),
  });
